import StudyService from "../services/study.service.js";
import Config from "@/config/config.js";
import { getSession } from "@/hooks/useSession.js";
import NavigationService from "../services/navigation.service.js";
import EmailController from "./email.controller.js";
import AuthService from "../services/auth.service.js";
import State, { STATE, CurrentStudyState, CurrentEntityState } from "@state";
import { sha256 } from "@/utils/crypto.js";
import ParticipantService from "../services/participant.service.js";

/**
 * Controller frontend pour uploader le consentement signé d’un patient.
 * @param {object} params – Contient patientId, studyId, siteIcfId, fileBlob, fileHash, consentId.
 * @returns {Promise<object>} – Résultat de l’upload ou erreur normalisée.
 */
const savePatientSignedConsent = async (params) => {
  console.log(
    "=== savePatientSignedConsent === consent.controller.js === key: 615060 ==="
  );
  console.dir(params, { depth: null, colors: true });
  console.log("=================================");

  const requiredFields = [
    "id",
    "participant_id",
    "site_icf_id",
    "participant_file",
    "participant_file_hash",
    "investigator_id",
    "study_id",
    "participant_signature",
  ];

  const missing = requiredFields.filter((key) => !params[key]);
  if (missing.length > 0) {
    console.warn("❌ Champs manquants:", missing.join(", "));
    return {
      success: false,
      message: `Champs requis manquants: ${missing.join(", ")}`,
      errorCode: "missing-upload-fields",
      errorKey: 615064,
    };
  }

  try {
    const result = await ParticipantService.savePatientSignedConsent({
      ...params,
      studyId: params.study_id,
    });

    console.log("✅ savePatientSignedConsent result:", result);
    return result;
  } catch (error) {
    console.error("❌ Erreur dans savePatientSignedConsent controller:", error);
    return {
      success: false,
      message: "Erreur lors de l'upload du consentement signé",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "upload-consent-controller-error",
      errorKey: 615065,
    };
  }
};

const uploadPatientConsent = async (params) => {
  let response = {};
  const session = getSession();

  const formData = new FormData();
  formData.append("protocolId", params.site_icf_id);
  formData.append("studyId", params.study_id);
  formData.append("participantId", params.participant_id);

  console.log('=== params === participant.controller.js === key: 006247 ===');
  console.dir(params, { depth: null, colors: true })
  console.log('=================================');

  // Fichier – s'assurer que c'est bien un File/Blob
  if (params.participant_file) {
    formData.append(
      "file",
      params.participant_file,
      params.participant_file.name
    );
  }

  // Obtenir une clé API temporaire unique pour chaque envoi
  const hashedEmail = await sha256(session.user.email);
  const tmpKeyRes = await AuthService.getTmpApiKey(hashedEmail);

  const xApiKey = tmpKeyRes;

  try {
    const res = await ParticipantService.uploadPatientConsent(
      formData,
      xApiKey
    );

    console.log("=== res === participant.controller.js === key: 251967 ===");
    console.dir(res, { depth: null, colors: true });
    console.log("=================================");

    if (res.success !== false) {
      params.site_icf_id = res.protocolId;
      params.integrityHash = res.integrityHash || "no-integrity-hash";
      delete params.protocolId;
    }
    response = {
      encryptedFile: res.encryptedFile.encryptedFile,
      signature: res.signature,
      consentId: res.consentId,
    };
  } catch (err) {
    response = err;
  }

  return response;
};

/**
 * Controller frontend pour révoquer un consentement signé d’un patient.
 * @param {object} params – Contient consent_id, participant_id?, site_icf_id?, studyId
 * @returns {Promise<object>} – Résultat de la révocation ou erreur normalisée.
 */

const revokePatientConsent = async (params) => {
  console.log(
    "=== revokePatientConsent === consent.controller.js === key: 832740 ==="
  );
  console.dir(params, { depth: null, colors: true });
  console.log("=================================");
  const requiredFields = ["consent_id", "studyId"];

  const missing = requiredFields.filter((key) => !params[key]);
  if (missing.length > 0) {
    console.warn("❌ Champs manquants:", missing.join(", "));
    return {
      success: false,
      message: `Champs requis manquants: ${missing.join(", ")}`,
      errorCode: "missing-revoke-fields",
      errorKey: 832741,
    };
  }

  try {
    const result = await ParticipantService.revokePatientConsent(params);

    console.log(" revokePatientConsent result:", result);
    return result;
  } catch (error) {
    console.error("Erreur dans revokePatientConsent controller:", error);
    return {
      success: false,
      message: "Erreur lors de la révocation du consentement",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "revoke-consent-controller-error",
      errorKey: 832742,
    };
  }
};

const uploadInvestigatorConfirmConsent = async (params) => {
  let response = {};
  const session = getSession();

  console.log("=== params === participant.controller.js === key: 934426 ===");
  console.dir(params, { depth: null, colors: true });
  console.log("=================================");

  const formData = new FormData();
  formData.append("protocolId", params.site_icf_id);
  formData.append("studyId", params.study_id);
  formData.append("consentId", params.consent_id);
  formData.append("participantId", params.participant_id);

  // Fichier – s'assurer que c'est bien un File/Blob
  if (params.participant_file) {
    formData.append(
      "file",
      params.participant_file,
      params.participant_file.name
    );
  }

  // Obtenir une clé API temporaire unique pour chaque envoi
  const hashedEmail = await sha256(session.user.email);
  const tmpKeyRes = await AuthService.getTmpApiKey(hashedEmail);

  const xApiKey = tmpKeyRes;

  try {
    const res = await ParticipantService.uploadInvestigatorConfirmConsent(
      formData,
      xApiKey
    );

    console.log("=== res === participant.controller.js === key: 205075 ===");
    console.dir(res, { depth: null, colors: true });
    console.log("=================================");

    if (res.success !== false) {
      params.site_icf_id = res.protocolId;
      params.integrityHash = res.integrityHash || "no-integrity-hash";
      delete params.protocolId;
    }

    response = {
      encryptedFile: res.encryptedFile.encryptedFile,
      signature: res.signature,
      consentId: res.consentId,
    };
  } catch (err) {
    response = err;
  }

  return response;
};

/**
 * Controller frontend pour uploader la signature de l’investigateur.
 * @param {object} params – Contient studyId, participant_id, investigator_id, site_icf_id, investigator_file, investigator_file_hash.
 * @returns {Promise<object>} – Résultat de l’upload ou erreur normalisée.
 */
const saveInvestigatorConfirmConsent = async (params) => {
  console.log(
    "=== saveInvestigatorSignedConsent === consent.controller.js === key: 726501 ==="
  );
  console.dir(params, { depth: null, colors: true });
  console.log("=================================");

  const requiredFields = [
    "studyId",
    "participant_id",
    "investigator_id",
    "site_icf_id",
    "investigator_file_hash",
    "investigator_signature",
  ];

  const missing = requiredFields.filter((key) => !params[key]);
  if (missing.length > 0) {
    console.warn(" Missing fields:", missing.join(", "));
    return {
      success: false,
      message: `Missing required fields: ${missing.join(", ")}`,
      errorCode: "investigator-missing-upload-fields",
      errorKey: 726502,
    };
  }

  try {
    const result = await ParticipantService.saveInvestigatorConfirmConsent({
      ...params,
      studyId: params.studyId || "",
    });

    console.log(" saveInvestigatorConfirmConsent result:", result);
    return result;
  } catch (error) {
    console.error(
      " Erreur dans uploadInvestigatorSignedConsent controller:",
      error
    );
    return {
      success: false,
      message:
        "Erreur lors de l'upload du consentement signé de l’investigateur",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "investigator-upload-consent-controller-error",
      errorKey: 726503,
    };
  }
};

const updateParticipantUserInfo = async (params) => {
  console.log(
    "=== updateParticipantUserInfo FE === participant.controller.js === key: 901241 ==="
  );
  console.dir(params, { depth: null, colors: true });

  const { studyId, participantId, updates } = params || {};
  const missing = [];
  if (!studyId) missing.push("studyId");
  if (!participantId) missing.push("participantId");
  if (!updates || typeof updates !== "object" || !Object.keys(updates).length)
    missing.push("updates");

  if (missing.length) {
    return {
      success: false,
      message: `Missing required fields: ${missing.join(", ")}`,
      errorCode: "update-participant-user-missing-fields",
      errorKey: 901242,
    };
  }

  const ALLOWED = [
    "firstname",
    "lastname",
    "email",
    "phone_number",
    "postal_address_1",
    "postal_address_2",
    "country",
    "city",
    "zipcode",
  ];
  const payload = Object.fromEntries(
    Object.entries(updates).filter(
      ([k, v]) => ALLOWED.includes(k) && v !== undefined
    )
  );

  if (payload.email && !/^\S+@\S+\.\S+$/.test(payload.email)) {
    return {
      success: false,
      message: "Invalid email format.",
      errorCode: "invalid-email",
      errorKey: 901243,
    };
  }

  try {
    const res = await ParticipantService.updateParticipantUserInfo({
      studyId,
      participantId,
      updates: payload,
    });
    return res;
  } catch (error) {
    console.error("FE Controller error in updateParticipantUserInfo:", error);
    return {
      success: false,
      message: "Unexpected error while updating participant user info.",
      errorCode: "participant-user-update-fe-controller-error",
      errorKey: 901244,
      fromError: !Config.IN_PROD ? error.message : null,
    };
  }
};

const getMilestonesForParticipant = async (participantId) => {
  console.log(
    "=== participantId === participant.controller.js === key: 833557 ==="
  );
  console.dir(participantId, { depth: null, colors: true });
  console.log("=================================");

  if (!participantId) {
    return {
      success: false,
      message: "Missing participantId",
      errorCode: "missing-participant-id",
      errorKey: 236706,
    };
  }

  try {
    const res = await ParticipantService.getMilestonesForParticipant(
      participantId
    );
    return res;
  } catch (error) {
    console.log("=== error === participant.controller.js === key: 589449 ===");
    console.dir(error, { depth: null, colors: true });
    console.log("=================================");
    return {
      success: false,
      message: "Unexpected error while getting milestones for participant.",
      errorCode: "participant-milestones-get-fe-controller-error",
      errorKey: 976007,
      fromError: !Config.IN_PROD ? error.message : null,
    };
  }
};

const addParticipantMilestone = async (params) => {
  console.log("=== params === participant.controller.js === key: 466377 ===");
  console.dir(params, { depth: null, colors: true });
  console.log("=================================");

  const session = getSession();

  const hashedEmail = await sha256(session.user.email);
  const tmpKeyRes1 = await AuthService.getTmpApiKey(hashedEmail);
  const tmpKeyRes2 = await AuthService.getTmpApiKey(hashedEmail);

  const xApiKeys= [tmpKeyRes1,tmpKeyRes2]

  if (tmpKeyRes1?.success === false || tmpKeyRes2?.success === false) {
    return xApiKeys;
  }

  if (!params) {
    return {
      success: false,
      message: "Missing participantMilestoneId",
      errorCode: "missing-participant-milestone-id",
      errorKey: 547849,
    };
  }

  try {
    const res = await ParticipantService.addParticipantMilestone(params,xApiKeys);
    return res;
  } catch (error) {
    console.log("=== error === participant.controller.js === key: 464025 ===");
    console.dir(error, { depth: null, colors: true });
    console.log("=================================");
    return {
      success: false,
      message: "Unexpected error while logging participant milestone.",
      errorCode: "participant-milestone-log-fe-controller-error",
      errorKey: 984938,
      fromError: !Config.IN_PROD ? error.message : null,
    };
  }
};

const getPatientConsentDocument = async (params) => {
  const session = getSession();
  let response = {};

  const hashedEmail = await sha256(session.user.email);
  const tmpKeyRes = await AuthService.getTmpApiKey(hashedEmail);
  const xApiKey = tmpKeyRes;

  try {
    const res = await ParticipantService.getPatientConsentDocument(
      params.participant_id,
      params.study_id, 
      xApiKey
    );

    console.log("=== res === participant.controller.js === key: getConsent ===");
    console.dir(res, { depth: null, colors: true });
    console.log("=================================");

    response = {
      success: true,
      ...res,
    };
  } catch (err) {
    console.error("❌ Error in getPatientConsentDocument:", err);
    response = {
      success: false,
      ...err,
    };
  }

  return response;
};

const ParticipantController = {
  uploadPatientConsent,
  savePatientSignedConsent,
  revokePatientConsent,
  updateParticipantUserInfo,
  uploadInvestigatorConfirmConsent,
  saveInvestigatorConfirmConsent,
  getMilestonesForParticipant,
  addParticipantMilestone,
  getPatientConsentDocument,
};

export default ParticipantController;
